import { DriftWall } from '@/components/molecules/drift-wall.client'
import { SCROLL_GALLERY_IMAGES } from '@/lib/home-scroll-gallery-config'
import { CONTAINER } from '@/lib/layout'
import { ACCENT_TEXT, HEADING, LEAD } from '@/lib/typography'
import { cn } from '@/lib/utils'

/**
 * The work, under the price list — a full-bleed wall of real thumbnails
 * drifting past the reader.
 *
 * Owner's ask, in his words: *„galerię miniatur możemy zrobić w zakładce
 * miniatury pod panelami z zakupem […] żeby miniatury sobie latały"*.
 *
 * ## WHY IT STANDS BELOW THE TIERS AND NOT ABOVE THEM
 *
 * Somebody on `/pl/miniaturki` has already decided they want thumbnails; what
 * they are doing on this page is choosing a package. Putting fifty pictures
 * above that choice pushes it off the screen. Underneath, the wall answers the
 * question the price list leaves open — *what do I actually get for 89 zł* —
 * at the moment it is asked.
 *
 * ## THE WHOLE GALLERY, NOT A SAMPLE
 *
 * The home page's tile takes every second cover, because it is a 300px box in
 * a bento grid and `items × copies` is how many `<Image>` elements the page
 * mounts. This is the page ABOUT thumbnails and it gets all 55. The cost is
 * bounded the same way: seven columns of eight covers is tall enough that
 * `DriftWall` needs its floor of three copies and no more.
 *
 * ## IT IS DECORATIVE, AND THAT IS DELIBERATE
 *
 * `DriftWall` is `aria-hidden` with empty `alt` on every tile — an endless
 * drifting wall is not something a screen reader can be walked through, and
 * 165 unlabelled images announced one after another is worse than silence. The
 * heading and the sentence above carry what the wall is; they are why this
 * section is `aria-labelledby` and not a bare `<div>`.
 */
const SHOTS = SCROLL_GALLERY_IMAGES.map((shot) => ({ src: shot.src }))

export function ThumbnailGalleryBand() {
  return (
    <section
      aria-labelledby="galeria-miniatur"
      className="relative mt-24 border-t border-white/[0.07] pt-16 pb-4 sm:pt-20"
    >
      <div className={cn(CONTAINER, 'relative z-10')}>
        <h2 id="galeria-miniatur" className={cn(HEADING, 'text-white')}>
          Pięćdziesiąt kilka miniatur. <span className={ACCENT_TEXT}>Wszystkie nasze.</span>
        </h2>
        <p className={cn(LEAD, 'mt-4 max-w-2xl text-white/60')}>
          Żadna nie jest przykładem z banku zdjęć — każda poszła na czyjś kanał. Ściana leci sama i
          zatrzymuje się, kiedy zjedziesz niżej.
        </p>
      </div>

      {/* The wall gets a HEIGHT and nothing else: `DriftWall` fills its parent
          and measures that box on every resize to decide how many copies of a
          column it needs to loop without a seam. A box with no height is a
          wall with no tiles. */}
      <div className="relative mt-10 h-[24rem] sm:h-[32rem] lg:h-[38rem]">
        <DriftWall
          items={SHOTS}
          // Seven columns rather than the tile's five: this band is the width
          // of the window, and five columns of 260px leave two thirds of a
          // desktop screen empty on either side of the plane.
          columns={7}
          // 16:9, because that is what a YouTube thumbnail is. The box is given
          // in pixels, so the two numbers have to agree by hand.
          tileWidth={260}
          tileHeight={146}
          gap={18}
          radius={14}
          // Slower than the home tile's 13, which is already slower than
          // upstream's 42. The owner's note there was *„muszą wolniej lecieć"*
          // and it applies twice over here: these are bigger tiles on the page
          // that exists to show them, so the eye has to be able to settle on
          // one and actually read it.
          speed={10}
          // Turned less than the home tile (−14°) and pushed further back. A
          // full-bleed plane at that angle puts its far edge past the side of
          // the window, so the outermost column is seen almost edge-on.
          turn={-9}
          tilt={13}
          depth={170}
          // The pictures come first: `dim` and `overlayOpacity` multiply, and
          // the scrim is here only to stop a white thumbnail glowing out of its
          // tile — not to tint the work.
          dim={0.96}
          overlayOpacity={0.1}
          fade={0.42}
          sizes="(max-width: 640px) 45vw, 260px"
        />
      </div>
    </section>
  )
}
